import { useState } from "react";
import { motion } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { CommentaryButton } from "./StorySection";
import { WindowBar } from "./SearchSection";
import { SECTIONS, EXPERIMENTS } from "@/data/content";

// Card image with coloured-panel fallback if the file is missing.
function CardImage({ src, alt, accent }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt || ""}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return null;
}

// BUILDING — workshop grid of evolving experiments, not a résumé.
export default function BuildingSection() {
  const { openCommentary } = useMatch();
  const section = SECTIONS.building;

  return (
    <div
      className="bg-[#F2F2F0] border-b-4 border-black py-16 sm:py-24 px-4 sm:px-8 lg:px-16"
      data-testid="building-section"
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-10 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.28em] uppercase px-4 py-2 mb-6"
              data-testid="building-minute"
            >
              {section.minuteEvent}
            </span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-black">
              {section.headline}
            </h2>
            <p className="mt-5 text-base sm:text-lg font-medium leading-relaxed text-black/75 max-w-xl">
              {section.preview}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <CommentaryButton
              onClick={() => openCommentary("building")}
              testId="building-commentary-btn"
            />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {EXPERIMENTS.map((exp, i) => (
            <motion.article
              key={exp.id}
              data-testid={`${exp.id}-card`}
              className="bg-white border-[3px] border-black hard-shadow-lg overflow-hidden transition-transform duration-200 hover:-translate-y-1.5"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              <WindowBar />
              <div
                className="aspect-[16/10] flex items-center justify-center border-b-[3px] border-black"
                style={{ backgroundColor: exp.accent }}
              >
                <CardImage src={exp.imageUrl} alt={exp.imageAlt} accent={exp.accent} />
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="font-broadcast font-extrabold text-xs sm:text-sm tracking-[0.18em] uppercase text-black mb-2.5">
                  {exp.title}
                </h3>
                <p className="text-sm font-medium leading-relaxed text-black/70">{exp.note}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}
